/**
 * FilterOptions.tsx
 * Description: Holds the provider that stores filter options that are retreived from an API
 */

// imports
 import { API } from 'aws-amplify';
import { useEffect, useState, createContext, useContext } from 'react';
import { error } from '../constants/bindings';
import { useApiError } from './ApiErrorProvider';
import { useAuth } from './Auth';

 // interface to create the context
 interface SearchOptionsState {
    positionOptions: string[];
    equipmentOptions: string[];
    baseOptions: any;
 }
// create the FilterOptionsContext
 const SearchOptionsContext = createContext({
    positionOptions: [],
    equipmentOptions: [],
    baseOptions: []
  } as SearchOptionsState);

 /**
  * This provider retrieves the filter options for the position and equipment filters that appear 
  *     on the pairing package page and the DBM report page
  * @param props Props passed to the provider 
  * @returns A context provider
  */
export const SearchOptions = (props) => {
    const {user} = useAuth();
    const {setErrorCode} = useApiError();
    const [positionOptions, setPositionOptions] = useState([]);
    const [equipmentOptions, setEquipmentOptions] = useState([]);
    const [baseOptions, setBaseOptions] = useState([]);
    // const [dateOptions, setDateOptions] = useState(null)

    // runs on component load
    useEffect(() => {
      if (!user) {
        return;
      }
      getEquipment();
      getPositions();
      getBase();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);


    /** retrievePositions
     * Makes a call to an API endpoint to retrieve a list of Position options
     * @returns list of position options
     */
    const getPositions = async () => {
      if (!user || positionOptions.length) {
        return;
      }
      try {
        const positionData = await API.get('crewlife', `/positions`, {});
        let updated = positionData.results.map(e => e.position);
        updated.push('All');
        setPositionOptions(updated);
      } catch (e:any) {
        if( e.response?.status) { setErrorCode(e.response?.status); }
        else { setErrorCode(error.DEFAULT); }
        return [];
      }
    };

    /** retrieveEquipment
     * Makes a call to an API endpoint to retrieve a list of Equipment options
     * @returns list of Equipment options
     */
    const getEquipment = async ()  => {
      if (!user || equipmentOptions.length) {
        return;
      }
      try {
        const equipmentData = await API.get('crewlife', `/equipments`, {});
        let updated = equipmentData.results.map(e => e.equipmentcode);
        updated.push('All');
        setEquipmentOptions(updated);
      } catch (e:any) {
        if( e.response?.status) { setErrorCode(e.response?.status); }
        else { setErrorCode(error.DEFAULT); }
        return [];
      }
    };

    /** retrieveBase
     * Makes a call to an API endpoint to retrieve a list of Base options
     * @returns list of Base options
     */
    const getBase = async () => {
      if (!user || baseOptions.length) {
        return;
      }
      try {
        const baseData = await API.get('crewlife', `/airports`, {});
        let updated = baseData.results.map(e => e['base_location']);
        updated.push('All');
        setBaseOptions(updated);
        // return Options.baseOptions
      } catch (e:any) {
        if( e.response?.status) { setErrorCode(e.response?.status); }
        else { setErrorCode(error.DEFAULT); }
        return [];
      }
    };


    return (
        <SearchOptionsContext.Provider value={{positionOptions,
          equipmentOptions,
          baseOptions}}>
            {props.children}
        </SearchOptionsContext.Provider>
    );
}

/** useSearchOptions
 * This is used by a component to retrieve the Filter Options Context
 * @returns The Filter Options Context 
 */
export const useSearchOptions = () => {
    return useContext(SearchOptionsContext);
}